import { useAiChatbotStream } from "@/hooks/use-ai-chatbot-stream";
import { useAiChatbotQuery } from "@/api/ai-chatbot-http-functions";
import { Button } from "@/components/ui/button";
import ErrorUI from "@/components/ui/error-ui";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { FeatureFlag } from "@/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";
import { z } from "zod";
import { PostMarkdown } from "../help/help-topic";

const schema = z.object({
    question: z.string().min(1, "Please enter a question"),
});

type FormValues = z.infer<typeof schema>;

const AiChatbotStreaming = () => {
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { question: "" },
    });

    const { start, streamedText, isStreaming, error, reset } =
        useAiChatbotStream();

    const onSubmit = ({ question }: FormValues) => {
        void start(question);
    };

    const question = useWatch({ control: form.control, name: "question" });

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="gap-wide flex w-full flex-col content-start items-stretch"
            >
                <FormField
                    control={form.control}
                    name="question"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Textarea
                                    {...field}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        reset();
                                    }}
                                    className="w-full"
                                    placeholder={`Ask a question, e.g. "Why is my charger showing as offline?"`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button
                    disabled={isStreaming || !question.length}
                    type="submit"
                    appearance="brand"
                    className="self-end"
                >
                    Ask Ken
                </Button>
                <Skeleton
                    className={cn(
                        "-mt-wide h-0 w-full transition-all duration-700",
                        isStreaming && !streamedText && "mt-0 h-60",
                    )}
                />
                {error ? (
                    <div className="px-default py-widest">
                        <ErrorUI
                            title={"Something went wrong. Please try again."}
                            description={error.message}
                        />
                    </div>
                ) : (
                    <div
                        className={cn(
                            "bg-neutral-subtlest-default rounded-default scale-y-0 transition-all duration-1000",
                            !!streamedText && "scale-y-100",
                        )}
                    >
                        {!!streamedText && (
                            <PostMarkdown>{streamedText}</PostMarkdown>
                        )}
                    </div>
                )}
            </form>
        </Form>
    );
};

const AiChatbotNonStreaming = () => {
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { question: "" },
    });

    const {
        mutate: fetchAnswer,
        isPending,
        error,
        data: response,
        reset: resetMutation,
    } = useAiChatbotQuery<Error>();

    const onSubmit = ({ question }: FormValues) => {
        resetMutation();
        fetchAnswer({ data: { question } });
    };

    const question = useWatch({ control: form.control, name: "question" });

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="gap-wide flex w-full flex-col content-start items-stretch"
            >
                <FormField
                    control={form.control}
                    name="question"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Textarea
                                    {...field}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        resetMutation();
                                    }}
                                    className="w-full"
                                    placeholder={`Ask a question, e.g. "Why is my charger showing as offline?"`}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button
                    disabled={isPending || !question.length}
                    type="submit"
                    appearance="brand"
                    className="self-end"
                >
                    Ask Ken
                </Button>
                <Skeleton
                    className={cn(
                        "-mt-wide h-0 w-full transition-all duration-700",
                        isPending && "mt-0 h-60",
                    )}
                />
                {error ? (
                    <div className="px-default py-widest">
                        <ErrorUI
                            title={"Something went wrong. Please try again."}
                            description={error.message}
                        />
                    </div>
                ) : (
                    <div
                        className={cn(
                            "bg-neutral-subtlest-default rounded-default scale-y-0 transition-all duration-1000",
                            !!response?.message && "scale-y-100",
                        )}
                    >
                        {!!response?.message && (
                            <PostMarkdown>{response.message}</PostMarkdown>
                        )}
                    </div>
                )}
            </form>
        </Form>
    );
};

const AiChatbot = () => {
    const flags = useFlags();
    const useStreaming = flags[FeatureFlag.IL_4148_STREAM_CHATBOT_RESPONSES];

    return useStreaming ? <AiChatbotStreaming /> : <AiChatbotNonStreaming />;
};

export default AiChatbot;
